import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import {
  UniversalFilterTaskStatusStatus,
  UniversalFilterTaskStatusValue,
  UniversalFilterTaskStatusModes,
  UniversalFilterTaskStatusFilterState,
  UniversalFilterTaskStatusOnChange
} from '../../UniversalFilterTaskStatus.types';

interface UniversalFilterTaskStatusFilterStateOptions {
  onChange: UniversalFilterTaskStatusOnChange;
}

function useUniversalFilterTaskStatusFilterState({
  onChange
}: UniversalFilterTaskStatusFilterStateOptions) {
  // All modes and options available in handleApplyFilterState
  // must be implemented in handleParseFilterValue too
  const handleApplyFilterState = useCallback<
    (filterState: UniversalFilterTaskStatusFilterState) => void
  >(
    (filterState) => {
      const statuses = filterState?.statuses;

      if (filterState?.mode === UniversalFilterTaskStatusModes.IS_ANY_OF) {
        onChange(isEmpty(statuses) ? undefined : { in: statuses });
        return;
      }

      if (filterState?.mode === UniversalFilterTaskStatusModes.IS_NONE_OF) {
        onChange(isEmpty(statuses) ? undefined : { notIn: statuses });
        return;
      }

      onChange();
    },
    [onChange]
  );

  const handleParseFilterValue = useCallback<
    (
      filterValue: UniversalFilterTaskStatusValue
    ) => UniversalFilterTaskStatusFilterState | undefined
  >((filterValue) => {
    if (isNil(filterValue)) {
      return undefined;
    }

    if (!isEmpty(filterValue.in)) {
      return {
        mode: UniversalFilterTaskStatusModes.IS_ANY_OF,
        statuses: filterValue.in as UniversalFilterTaskStatusStatus[]
      };
    }

    if (!isEmpty(filterValue.notIn)) {
      return {
        mode: UniversalFilterTaskStatusModes.IS_NONE_OF,
        statuses: filterValue.notIn as UniversalFilterTaskStatusStatus[]
      };
    }

    return undefined;
  }, []);

  return { handleApplyFilterState, handleParseFilterValue };
}

export default useUniversalFilterTaskStatusFilterState;
